/**
 * Frame-budget adaptive quality — coalesces work when the machine is drowning.
 *
 * Mutates only **working copies** of RenderParams / SimParams for the current
 * frame. Never touches the user's saved prefs (URL, Lab sliders stay as set).
 * When load forces a backoff, `report()` lists what is temporarily paused and
 * which levers (n, speed, features) restore full quality — so the system is
 * not changing settings "from under" the user without feedback.
 *
 * Ladder (hysteresis — consecutive frames, not a raw EMA cross):
 *   Q0  full quality
 *   Q1  drop edges / grid / gnomon / velocity vectors
 *   Q2  drop trails
 *   Q3  simplify point color + rect glyphs
 *   Q4  **sim throttle**: ½ step rate; mutual gravity → COM (O(n) not O(n·k))
 *   Q5  **CPU guard**: ¼ step rate; gravity off; local-topology off (unless Reynolds)
 *
 * Critical: Frozen Graph + friend edges used to oscillate Q0↔Q1 every few frames
 * and strobe the edge overlay — upgrade/downgrade holds prevent that.
 */

import {
  cloneAudioParams,
  type AudioParams,
} from "../audio/params";
import {
  cloneRenderParams,
  cloneSimParams,
  type RenderParams,
  type SimParams,
} from "../sim/params";

export type QualityLevel = 0 | 1 | 2 | 3 | 4 | 5;

/** Structured feedback for HUD + stage chip (prefs never rewritten). */
export type QualityReport = {
  level: QualityLevel;
  /** True when any temporary backoff is active. */
  active: boolean;
  /** Compact HUD suffix, e.g. " · Q4 load". Empty at Q0. */
  shortLabel: string;
  /** Stage chip one-liner (null when inactive). */
  chip: string | null;
  /** Severity for chip styling. */
  severity: "ok" | "warn" | "critical";
  /** Features the user has on that are temporarily paused this frame. */
  paused: string[];
  /** Concrete levers that free budget (multiplicative: n × speed × features). */
  levers: string[];
  /** Multi-line detail for tooltip / title. */
  detail: string;
};

/** Enter level L+1 when EMA exceeds this (ms). Index = current level. */
const ENTER_MS = [14, 18, 24, 40, 55, 80] as const;
/** Leave level L toward L−1 only when EMA stays under this (ms). */
const LEAVE_MS = [9, 12, 16, 28, 40, 55] as const;
/** Consecutive expensive frames before upgrading (normal). */
const UPGRADE_HOLD = 12;
/** Faster climb when already well past 30 fps (frame ≳ 50 ms). */
const UPGRADE_HOLD_CRITICAL = 4;
/** Consecutive cheap frames before downgrading. */
const DOWNGRADE_HOLD = 45;
/** ~30 fps floor — sustained below this ratchets quality up. */
const TARGET_FRAME_MS = 1000 / 30;

export class AdaptiveQuality {
  level: QualityLevel = 0;
  private emaMs = 8;
  private readonly enabled: boolean;
  private upgradeStreak = 0;
  private downgradeStreak = 0;

  constructor(opts?: { enabled?: boolean }) {
    this.enabled = opts?.enabled !== false;
  }

  /**
   * Call once per live frame with step+draw wall time (ms).
   * No-op when disabled.
   */
  noteFrameCost(ms: number): void {
    if (!this.enabled) return;
    const cost = Number.isFinite(ms) && ms > 0 ? ms : 0;
    this.emaMs = this.emaMs * 0.9 + cost * 0.1;
    this.ratchetFromEma();
  }

  /**
   * Optional second signal from the HUD FPS window (~2 Hz).
   * If measured fps stays under ~30, treat as a heavy frame so we climb
   * even when a single long step made RAF timing look odd.
   */
  noteFps(fps: number): void {
    if (!this.enabled) return;
    if (!(fps > 0) || !Number.isFinite(fps)) return;
    if (fps < 30) {
      // Map fps → implied frame ms and blend harder when far below target.
      const implied = 1000 / Math.max(fps, 1);
      this.emaMs = this.emaMs * 0.7 + implied * 0.3;
      this.ratchetFromEma();
    }
  }

  private ratchetFromEma(): void {
    const atMax = this.level >= 5;
    const enterNext = !atMax ? ENTER_MS[this.level]! : Infinity;
    const leaveCur = this.level > 0 ? LEAVE_MS[this.level]! : 0;
    const critical = this.emaMs >= TARGET_FRAME_MS * 1.5; // ≳ 50 ms
    const upgradeHold = critical ? UPGRADE_HOLD_CRITICAL : UPGRADE_HOLD;

    if (!atMax && this.emaMs > enterNext) {
      this.upgradeStreak++;
      this.downgradeStreak = 0;
      if (this.upgradeStreak >= upgradeHold) {
        this.level = (this.level + 1) as QualityLevel;
        this.upgradeStreak = 0;
      }
    } else if (this.level > 0 && this.emaMs < leaveCur) {
      this.downgradeStreak++;
      this.upgradeStreak = 0;
      if (this.downgradeStreak >= DOWNGRADE_HOLD) {
        this.level = (this.level - 1) as QualityLevel;
        this.downgradeStreak = 0;
      }
    } else {
      this.upgradeStreak = 0;
      this.downgradeStreak = 0;
    }
  }

  /**
   * Derive draw params from user prefs + current quality level.
   * Pass `{ full: true }` when paused / static so overlays never strobe.
   */
  apply(user: RenderParams, opts?: { full?: boolean }): RenderParams {
    if (!this.enabled || this.level === 0 || opts?.full) return user;
    const r = cloneRenderParams(user);
    if (this.level >= 1) {
      r.showFriendEdges = false;
      r.showEnemyEdges = false;
      r.showGrid = false;
      r.showGnomon = false;
      r.showVelocityVectors = false;
    }
    if (this.level >= 2) {
      r.showTrails = false;
    }
    if (this.level >= 3) {
      r.velocityColor = false;
      r.colorMode = "uniform";
      r.pointStyle = "rect";
    }
    return r;
  }

  /**
   * Working-copy sim params for this frame's step only.
   * Returns the same reference when no sim degradation applies.
   */
  applySim(user: SimParams): SimParams {
    if (!this.enabled || this.level < 4) return user;
    const s = cloneSimParams(user);
    if (this.level >= 4) {
      // Mutual N-body is the usual CPU bomb; fall back to a single COM well.
      if (s.gravityMode === "mutual") s.gravityMode = "com";
    }
    if (this.level >= 5) {
      // Emergency: stop gravity entirely; drop local rechoose sampling cost.
      s.gravityMode = "none";
      if (s.model !== "reynolds") {
        s.localTopology = false;
        // spatialHash alone is cheap if unused; localTopology was the sampler.
      }
    }
    return s;
  }

  /**
   * Multiplier on the user's steps-per-frame (playback speed).
   * Q4 → ½, Q5 → ¼ so the sim cannot outrun the frame budget.
   */
  stepScale(): number {
    if (!this.enabled) return 1;
    if (this.level >= 5) return 0.25;
    if (this.level >= 4) return 0.5;
    return 1;
  }

  /**
   * Working-copy audio for this frame only (prefs sacred).
   * Q3+ mute topology chorus; Q4+ mute kinematics choir; Q5 master mute.
   */
  applyAudio(
    user: AudioParams,
    opts?: { full?: boolean },
  ): AudioParams {
    if (!this.enabled || this.level === 0 || opts?.full) return user;
    if (!user.enabled) return user;
    const a = cloneAudioParams(user);
    if (this.level >= 3) a.topologyVoice = false;
    if (this.level >= 4) a.kinematicsVoice = false;
    if (this.level >= 5) {
      a.enabled = false;
      a.masterGain = 0;
    }
    return a;
  }

  /**
   * Explicit feedback: what is temporarily paused vs user prefs, and which
   * levers free budget. Lab values are never rewritten — only listed.
   */
  report(
    userSim: SimParams,
    userRender: RenderParams,
    ctx?: {
      stepsPerFrame?: number;
      fps?: number;
      userAudio?: AudioParams;
    },
  ): QualityReport {
    const level = this.enabled ? this.level : 0;
    if (level === 0) {
      return {
        level: 0,
        active: false,
        shortLabel: "",
        chip: null,
        severity: "ok",
        paused: [],
        levers: [],
        detail: "",
      };
    }

    const paused: string[] = [];
    const levers: string[] = [];

    // —— What user has ON that we are temporarily holding off ——
    if (level >= 1) {
      if (userRender.showFriendEdges) paused.push("friend edges");
      if (userRender.showEnemyEdges) paused.push("enemy edges");
      if (userRender.showGrid) paused.push("grid");
      if (userRender.showGnomon) paused.push("gnomon");
      if (userRender.showVelocityVectors) paused.push("velocity vectors");
    }
    if (level >= 2 && userRender.showTrails) paused.push("trails");
    if (level >= 3) {
      const fancy =
        userRender.colorMode === "velocity" ||
        userRender.colorMode === "cycle" ||
        userRender.velocityColor;
      if (fancy) paused.push("speed/cycle color → uniform");
      if (userRender.pointStyle === "circle")
        paused.push("circle points → rect");
      const ua = ctx?.userAudio;
      if (ua?.enabled && ua.topologyVoice) paused.push("topology chorus");
    }
    if (level >= 4) {
      const ua = ctx?.userAudio;
      if (ua?.enabled && ua.kinematicsVoice) paused.push("kinematics choir");
    }
    if (level >= 5) {
      const ua = ctx?.userAudio;
      if (ua?.enabled) paused.push("audio muted");
    }
    if (level >= 4 && level < 5) {
      const scale = this.stepScale();
      paused.push(`step rate ${scale}× (your speed setting kept)`);
      if (userSim.gravityMode === "mutual") {
        paused.push("mutual gravity → COM bowl");
      }
    }
    if (level >= 5) {
      const scale = this.stepScale();
      paused.push(`step rate ${scale}× (your speed setting kept)`);
      if (userSim.gravityMode !== "none") {
        paused.push(
          userSim.gravityMode === "mutual"
            ? "mutual gravity fully off"
            : `${userSim.gravityMode} gravity off`,
        );
      }
      if (userSim.localTopology && userSim.model !== "reynolds") {
        paused.push("local topology sampling");
      }
    }

    // Deduplicate paused lines (mutual path can double-note gravity)
    const pausedUnique = [...new Set(paused)];

    // —— Levers: multiplicative blow-up = n × speed × features ——
    const n = userSim.n;
    const spf = ctx?.stepsPerFrame ?? 1;
    if (n >= 1500) levers.push(`lower n (now ${n})`);
    else if (n >= 800) levers.push(`try fewer particles (n=${n})`);
    if (spf > 1) levers.push(`lower playback speed (now ${spf}×)`);
    if (userSim.gravityMode === "mutual") {
      levers.push("switch Gravity off mutual (COM/origin is cheaper)");
    } else if (userSim.gravityMode !== "none" && level >= 4) {
      levers.push("lower Gravity strength or turn Gravity off");
    }
    if (userSim.localTopology) levers.push("turn off local topology");
    if (userSim.spatialHash && userSim.model !== "reynolds") {
      // hash alone is fine; only mention if paired with heavy draw
    }
    if (userRender.showFriendEdges || userRender.showEnemyEdges) {
      levers.push("turn off friend/enemy edges");
    }
    if (userRender.showTrails) levers.push("turn off trails");
    if (userRender.showVelocityVectors)
      levers.push("turn off velocity vectors");
    if (ctx?.userAudio?.enabled) {
      levers.push("turn off Audio or lower n/speed");
    }
    if (level >= 3 && userRender.colorMode !== "uniform") {
      levers.push("Color by → Uniform");
    }
    if (userSim.motionMode === "inertial") {
      levers.push("Integrator → Kinematic (slightly cheaper)");
    }
    // Always offer the generic recovery path
    if (levers.length === 0) {
      levers.push("lower n, playback speed, or expensive lenses");
    }

    const severity: QualityReport["severity"] =
      level >= 5 ? "critical" : level >= 4 ? "warn" : "warn";

    const head =
      level >= 5 ? "CPU guard" : level >= 4 ? "Sim eased" : "Overlays eased";

    const pausedBit =
      pausedUnique.length > 0
        ? `Paused: ${pausedUnique.slice(0, 4).join(", ")}${pausedUnique.length > 4 ? "…" : ""}`
        : "Easing draw cost";
    const leverBit = `To restore: ${levers.slice(0, 3).join(" · ")}`;

    const fpsBit =
      ctx?.fps != null && ctx.fps > 0 && ctx.fps < 30
        ? ` · ${ctx.fps.toFixed(0)} fps`
        : "";

    const chip = `Q${level} ${head}${fpsBit} — ${pausedBit}. ${leverBit}`;

    const detailLines = [
      `Load guard Q${level} — your Lab settings are unchanged; this is a temporary backoff.`,
      pausedUnique.length
        ? `Temporarily paused: ${pausedUnique.join("; ")}.`
        : null,
      `Free budget (n × speed × features multiply): ${levers.join("; ")}.`,
      `Frame cost EMA ~${this.emaMs.toFixed(0)} ms (target ≲ ${TARGET_FRAME_MS.toFixed(0)} ms / 30 fps).`,
    ].filter(Boolean) as string[];

    return {
      level,
      active: true,
      shortLabel: ` · Q${level} load`,
      chip,
      severity,
      paused: pausedUnique,
      levers,
      detail: detailLines.join(" "),
    };
  }

  /**
   * While paused / static: clear streaks and gently drop level so a later
   * resume doesn't stay stuck with edges stripped from an old load spike.
   */
  cool(): void {
    this.upgradeStreak = 0;
    this.downgradeStreak = 0;
    this.emaMs = Math.min(this.emaMs, 10);
    if (this.level > 0) {
      this.level = (this.level - 1) as QualityLevel;
    }
  }

  /** EMA frame cost in ms (for tests / HUD diagnostics). */
  get emaFrameMs(): number {
    return this.emaMs;
  }

  /** @deprecated prefer report().shortLabel — kept for call sites mid-migration */
  label(): string {
    if (!this.enabled || this.level === 0) return "";
    return ` · Q${this.level} load`;
  }
}
